import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { LanguageProvider } from "@/hooks/useLanguage";
import { Capacitor } from "@capacitor/core";

// Eager load only critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Layout from "./components/Layout";

// Chunk loading error handler - auto-reloads once on deployment cache issues
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    const hasRefreshed = sessionStorage.getItem('chunk-load-retry');
    try {
      sessionStorage.setItem('chunk-load-retry', 'true');
      const component = await componentImport();
      sessionStorage.removeItem('chunk-load-retry');
      return component;
    } catch (error) {
      if (!hasRefreshed && error instanceof TypeError && error.message.includes('Failed to fetch')) {
        sessionStorage.removeItem('chunk-load-retry');
        window.location.reload();
        return { default: () => null };
      }
      sessionStorage.removeItem('chunk-load-retry');
      throw error;
    }
  });

// Lazy load all other pages for better performance
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Workout = lazyWithRetry(() => import("./pages/Workout"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Friends = lazyWithRetry(() => import("./pages/Friends"));
const Templates = lazyWithRetry(() => import("./pages/Templates"));
const Leaderboard = lazyWithRetry(() => import("./pages/Leaderboard"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Achievements = lazyWithRetry(() => import("./pages/Achievements"));
const StrengthCalculator = lazyWithRetry(() => import("./pages/StrengthCalculator"));
const Premium = lazyWithRetry(() => import("./pages/Premium"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const ClaimWorkout = lazyWithRetry(() => import("./pages/ClaimWorkout"));
const BodyAssessment = lazyWithRetry(() => import("./pages/BodyAssessment"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SubscriptionSuccess = lazyWithRetry(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazyWithRetry(() => import("./pages/SubscriptionCancel"));
const ExerciseDemo = lazyWithRetry(() => import("./pages/ExerciseDemo"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

// Component to handle early permission requests on native
const NativePermissions = () => {
  useEffect(() => {
    const requestPermissions = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Request notification permissions early
          const { LocalNotifications } = await import('@capacitor/local-notifications');
          const result = await LocalNotifications.requestPermissions();
          console.log('Notification permissions:', result.display);
          
          // Preload the timer sound
          const { NativeAudio } = await import('@capacitor-community/native-audio');
          await NativeAudio.preload({
            assetId: 'timer-complete',
            assetPath: 'public/sounds/timer-complete.mp3',
            audioChannelNum: 1,
            isUrl: false,
          });
          console.log('Timer sound preloaded');
        } catch (error) {
          console.log('Native permissions/audio setup:', error);
        }
      }
    };
    requestPermissions();
  }, []);
  
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <NativePermissions />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/subscription-success" element={<SubscriptionSuccess />} />
              <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
              <Route element={<Layout />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="workout" element={<Workout />} />
                <Route path="templates" element={<Templates />} />
                <Route path="leaderboard" element={<Leaderboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="achievements" element={<Achievements />} />
                <Route path="strength-calculator" element={<StrengthCalculator />} />
                <Route path="premium" element={<Premium />} />
                <Route path="friends" element={<Friends />} />
                <Route path="profile" element={<Profile />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="admin" element={<Admin />} />
                <Route path="body-assessment" element={<BodyAssessment />} />
                <Route path="claim-workout/:workoutId" element={<ClaimWorkout />} />
                <Route path="exercise-demo" element={<ExerciseDemo />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
